/** SoundCloud-style mini player — queue prev/next on Shop; pause + stop. */
import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import * as api from './api';
import { trackBeatFavorited } from './analytics';
import { usePreviewPlayerOptional } from './PreviewPlayer';
import { mmss } from './types';
import { BeatThumb, BsThemeContext, HeartIcon, s } from './ui';

const ctrlBtn = (disabled?: boolean) =>
  s(
    'display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;border:none;flex-shrink:0;' +
    (disabled
      ? 'background:transparent;color:var(--bs-text-faint);opacity:0.35;cursor:not-allowed'
      : 'background:transparent;color:var(--bs-text-body);cursor:pointer')
  );

export const MiniPlayerBar: React.FC = () => {
  const ctx = usePreviewPlayerOptional();
  const theme = useContext(BsThemeContext);
  if (!ctx?.track) return null;
  const {
    track, playing, currentTime, toggle, setFavorited, stop,
    queue, canPrev, canNext, playPrev, playNext,
  } = ctx;
  const queueMode = queue.length > 0;
  const metaParts = [
    mmss(currentTime),
    track.bpm != null && track.musical_key ? `${track.bpm} BPM · ${track.musical_key}` : null,
    queueMode ? `${Math.max(1, queue.findIndex((t) => t.id === track.id) + 1)}/${queue.length}` : null,
  ].filter(Boolean);

  const toggleFav = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!track) return;
    const next = !track.is_favorited;
    setFavorited(track.id, next);
    try {
      await api.setFavorite(track.id, next);
      trackBeatFavorited(track.id, next);
    } catch {
      setFavorited(track.id, !next);
    }
  };

  const controls = (
    <div className="bs-miniplayer-controls">
      {queueMode && (
        <button type="button" onClick={playPrev} disabled={!canPrev} aria-label="Previous beat" style={ctrlBtn(!canPrev)}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 2.5v11h1.6V9.2L12.5 13.5V2.5L5.1 6.8V2.5H3.5z" /></svg>
        </button>
      )}

      <button
        type="button"
        className="bs-miniplayer-play"
        onClick={() => toggle()}
        aria-label={playing ? 'Pause preview' : 'Play preview'}
        style={s(
          'width:44px;height:44px;border-radius:50%;border:none;flex-shrink:0;cursor:pointer;' +
          'background:var(--bs-btn-bg);color:var(--bs-btn-text);display:flex;align-items:center;justify-content:center'
        )}
      >
        {playing ? (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5z" /></svg>
        )}
      </button>

      {queueMode && (
        <button type="button" onClick={playNext} disabled={!canNext} aria-label="Next beat" style={ctrlBtn(!canNext)}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M12.5 2.5v11h-1.6V9.2L3.5 13.5V2.5l7.4 4.3V2.5h1.6z" /></svg>
        </button>
      )}
    </div>
  );

  const bar = (
    <div
      className={theme === 'dark' ? 'beatshop bs-dark' : 'beatshop'}
      style={s("font-family:'Inter',system-ui,sans-serif;-webkit-font-smoothing:antialiased")}
    >
      <div className="bs-miniplayer">
        {controls}

        <div className="bs-miniplayer-thumb">
          <BeatThumb size={40} />
        </div>

        <div className="bs-miniplayer-info">
          <div className="bs-miniplayer-title">{track.name}</div>
          <div className="bs-miniplayer-meta">{metaParts.join(' · ')}</div>
        </div>

        <button
          type="button"
          className="bs-heart bs-miniplayer-fav"
          onClick={toggleFav}
          title="Save to favorites"
          style={s('display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;border:none;background:transparent;cursor:pointer;flex-shrink:0')}
        >
          <HeartIcon filled={track.is_favorited} size={18} />
        </button>

        <button
          type="button"
          className="bs-miniplayer-close"
          onClick={stop}
          aria-label="Close player"
          title="Close"
          style={s('display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:8px;border:none;background:transparent;color:var(--bs-text-faint);cursor:pointer;flex-shrink:0;font-size:18px;line-height:1')}
        >
          ✕
        </button>
      </div>
    </div>
  );

  return createPortal(bar, document.body);
};
